import Box from '@mui/material/Box';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { isCordova } from '../../../util/mobile/deviceDetection';
import { isMobileSize } from '../../../util/mobile/mobile';
import { injectIntl } from '../../../util/reactIntl';
import IconSpinner from '../../Icons/IconSpinner/IconSpinner';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';

const KEY_CODE_LEFT_ARROW = 37;
const KEY_CODE_RIGHT_ARROW = 39;

const IMAGE_MARKER_CLASS = 'ImageCarousel-image';
const LOADING_MARKER_CLASS = 'ImageCarousel-loading';

// .root from ImageCarousel.module.css (applied only when no rootClassName override)
const rootSx = (theme) => ({
  position: 'relative',
  width: '100%',
  height: '100vh',
  padding: 0,
  [theme.breakpoints.up('md')]: {
    padding: '100px 10vw',
  },
});

// .imageWrapper + nested .loading/.loadingVisible/.image/.imageLoading child rules
const imageWrapperSx = (theme) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  // .image
  [`& .${IMAGE_MARKER_CLASS}`]: {
    // Fit image in the available space as big as possible, keeping the aspect ratio
    objectFit: 'contain',
  },
  // .imageLoading
  [`& .${IMAGE_MARKER_CLASS}.${IMAGE_MARKER_CLASS}--loading`]: {
    opacity: 0.5,
  },
  // .loading
  [`& .${LOADING_MARKER_CLASS}`]: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '50px',
    height: '50px',
    marginLeft: '-25px',
    marginTop: '-25px',
    opacity: 0,
  },
  // .loadingVisible
  [`& .${LOADING_MARKER_CLASS}.${LOADING_MARKER_CLASS}--visible`]: {
    opacity: 1,
    transition: `opacity ${theme.tokens.transitionStyle}`,
  },
});

// .imageIndex
const imageIndexSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightSemiBold,
  position: 'absolute',
  top: '21px',
  left: '20px',
  margin: 0,
  color: theme.tokens.matterColorLight,
  [theme.breakpoints.up('md')]: {
    margin: 0,
    fontWeight: theme.tokens.fontWeightSemiBold,
  },
});

// Shared .prev/.next declarations
const navButtonSx = (theme) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '20vw',
  border: 'none',
  borderRadius: 0,
  outline: 'none',
  cursor: 'pointer',
  backgroundSize: '13px auto',
  [theme.breakpoints.up('md')]: {
    backgroundSize: '20px auto',
  },
  [theme.breakpoints.up('lg')]: {
    opacity: 0.5,
    transition: theme.tokens.transitionStyleButton,
    '&:hover': {
      opacity: 1,
    },
  },
});

// .prev
const prevSx = (theme) => ({
  ...navButtonSx(theme),
  left: 0,
  backgroundImage:
    'url(\'data:image/svg+xml;utf8,<svg width="7" height="10" viewBox="0 0 7 10" xmlns="http://www.w3.org/2000/svg"><path d="M5.667 1c.094 0 .188.04.254.118.12.14.102.35-.038.47L1.85 5l4.032 3.412c.14.12.158.33.04.47-.12.14-.33.158-.47.04L1.117 5.254C1.043 5.19 1 5.098 1 5s.043-.19.118-.255L5.45 1.08c.064-.054.14-.08.217-.08" stroke="%23FFF" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  backgroundPosition: 'center left 20px',
  [theme.breakpoints.up('md')]: {
    backgroundPosition: 'center left 5vw',
    backgroundImage:
      'url(\'data:image/svg+xml;utf8,<svg width="20" height="31" viewBox="0 0 20 31" xmlns="http://www.w3.org/2000/svg"><path d="M18.6667957 0c.37866928 0 .75333854.1550012 1.0186737.4585452.47466996.5425042.4040028 1.3575521-.1573344 1.8199723L3.3986902 15.5001192 19.528135 28.72172085c.5613372.46242022.63200436 1.27746815.1573344 1.821264-.47733663.54508752-1.31734243.61096303-1.880013.15241784L.4720033 16.4869601C.1720012 16.2402499 0 15.8798721 0 15.5001192c0-.3797529.1720012-.7401307.4720033-.9868409L17.8054564.3048357C18.0561248.1007508 18.3627936 0 18.6667957 0" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  },
});

// .next
const nextSx = (theme) => ({
  ...navButtonSx(theme),
  right: 0,
  backgroundImage:
    'url(\'data:image/svg+xml;utf8,<svg width="7" height="10" viewBox="0 0 7 10" xmlns="http://www.w3.org/2000/svg"><path d="M1.333 9c-.094 0-.188-.04-.254-.118-.12-.14-.102-.35.038-.47L5.15 5 1.118 1.588c-.14-.12-.158-.33-.04-.47.12-.14.33-.158.47-.04l4.334 3.667C5.957 4.81 6 4.902 6 5s-.043.19-.118.255L1.55 8.92c-.064.054-.14.08-.217.08" stroke="%23FFF" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  backgroundPosition: 'center right 20px',
  [theme.breakpoints.up('md')]: {
    backgroundPosition: 'center right 5vw',
    backgroundImage:
      'url(\'data:image/svg+xml;utf8,<svg width="20" height="31" viewBox="0 0 20 31" xmlns="http://www.w3.org/2000/svg"><path d="M1.3332 31c-.37866 0-.75333-.1550012-1.01867-.4585452-.47467-.5425042-.404-1.3575521.15734-1.8199723l16.12944-13.2216017L.47187 2.27827915C-.08947 1.81585893-.16014 1.000811.31453.45701515.79187-.08807237 1.63187-.15394788 2.19454.3045973L19.528 14.5130399c.3.2467102.472.607088.472.9868409 0 .3797529-.172.7401307-.472.9868409L2.19454 30.6951643C1.94388 30.8992492 1.63721 31 1.3332 31" fill="%23FFF" fill-rule="evenodd"/></svg>\')',
  },
});

const pinchZoomWrapperSx = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
};

/**
 * @typedef {object} ContainerProps
 * @property {boolean} surroundImage - Whether the image is a surround image
 * @property {() => void} next - Function to go to next image
 * @property {() => void} prev - Function to go to previous image
 * @property {React.ReactNode} children - Child components to render
 */

/** @type {React.FC<ContainerProps>} */
const Container = React.memo(({ surroundImage, next, prev, children }) => {
  if (!surroundImage) {
    return (
      <Swipeable onSwipedLeftCallback={next} onSwipedRightCallback={prev}>
        {children}
      </Swipeable>
    );
  } else {
    return children;
  }
});

Container.displayName = 'Container';

/**
 * @typedef {object} ImageCarouselProps
 * @property {string} [rootClassName] - Root class name override
 * @property {string} [className] - Additional class name
 * @property {Array<object>} images - Images to display
 * @property {boolean} isOpen - Whether the carousel is open
 * @property {object} intl - react-intl object (injected)
 * @property {number} [initialIndex] - Index of the image shown first (default 0; out-of-range values fall back to 0)
 */

/** @type {React.FC<ImageCarouselProps>} */
const ImageCarouselComponent = ({
  rootClassName = null,
  className = null,
  images,
  isOpen,
  intl,
  initialIndex = 0,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(
    initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0,
  );
  const [selectedImageLoaded, setSelectedImageLoaded] = useState(false);

  const prev = useCallback(() => {
    const count = images.length;
    if (count < 2) {
      return;
    }
    setSelectedImageIndex((current) => (count + current - 1) % count);
    setSelectedImageLoaded(false);
  }, [images.length]);

  const next = useCallback(() => {
    const count = images.length;
    if (count < 2) {
      return;
    }
    setSelectedImageIndex((current) => (count + current + 1) % count);
    setSelectedImageLoaded(false);
  }, [images.length]);

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.keyCode === KEY_CODE_LEFT_ARROW) {
        prev();
      } else if (e.keyCode === KEY_CODE_RIGHT_ARROW) {
        next();
      }
    };
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [prev, next]);

  const naturalIndex = selectedImageIndex + 1;
  const imageIndex =
    images.length > 0 ? (
      <Box component="span" sx={imageIndexSx}>
        {naturalIndex}/{images.length}
      </Box>
    ) : null;
  const prevButton =
    images.length > 1 ? <Box component="button" onClick={prev} sx={prevSx} /> : null;
  const nextButton =
    images.length > 1 ? <Box component="button" onClick={next} sx={nextSx} /> : null;

  const imageAltText = intl.formatMessage(
    {
      id: 'ImageCarousel.imageAltText',
    },
    {
      index: naturalIndex,
      count: images.length,
    },
  );

  const markImageLoaded = (index) => () => {
    // Only mark the image loaded if the current index hasn't changed,
    // i.e. user hasn't already changed to another image index.
    setSelectedImageLoaded((loaded) => (selectedImageIndex === index ? true : loaded));
  };

  const currentImageIsLoaded = images.length === 0 || selectedImageLoaded;
  const loadingIconClasses = !currentImageIsLoaded
    ? `${LOADING_MARKER_CLASS} ${LOADING_MARKER_CLASS}--visible`
    : LOADING_MARKER_CLASS;
  const imageClasses = !currentImageIsLoaded
    ? `${IMAGE_MARKER_CLASS} ${IMAGE_MARKER_CLASS}--loading`
    : IMAGE_MARKER_CLASS;

  const image = (
    <ResponsiveImage
      alt={imageAltText}
      className={imageClasses}
      image={images[selectedImageIndex]}
      onError={markImageLoaded(selectedImageIndex)}
      onLoad={markImageLoaded(selectedImageIndex)}
      sizes="(max-width: 767px) 100vw, 80vw"
      variants={['scaled-xlarge', 'scaled-large', 'scaled-medium', 'scaled-small']}
    />
  );

  const pinchZoomForCordova = isCordova() ? (
    <Box sx={pinchZoomWrapperSx}>
      <TransformWrapper initialScale={1}>
        <TransformComponent>{image}</TransformComponent>
      </TransformWrapper>
    </Box>
  ) : (
    image
  );

  return (
    <div>
      {isOpen ? (
        <Container next={next} prev={prev} surroundImage={images[selectedImageIndex]?.surroundImage}>
          <Box
            className={[rootClassName, className].filter(Boolean).join(' ') || undefined}
            sx={rootClassName ? undefined : rootSx}
          >
            <Box sx={imageWrapperSx}>
              <IconSpinner className={loadingIconClasses} />
              {images[selectedImageIndex]?.surroundImage ? (
                <SurroundImageView
                  url={images[selectedImageIndex].attributes.variants['surround-crop'].url}
                />
              ) : (
                <>{pinchZoomForCordova}</>
              )}
            </Box>
            {imageIndex}
            {prevButton}
            {nextButton}
          </Box>
        </Container>
      ) : null}
    </div>
  );
};

ImageCarouselComponent.displayName = 'ImageCarousel';

const SurroundImageView = (props) => {
  const [viewer, setViewer] = useState(<></>);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('@vansite/pannellum-react').then((module) => {
        const { Pannellum } = module;
        setViewer(
          <Pannellum
            autoLoad={true}
            disableKeyboardCtrl={false}
            height="100%"
            hfov={isMobileSize() ? 65 : 100}
            image={props.url}
            keyboardZoom={false}
            mouseZoom={false}
            showControls={false}
            showZoomCtrl={false}
            width="100%"
          />,
        );
      });
    } else {
      setViewer(null);
    }
  }, [ref.current, props.url]);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      {viewer}
    </div>
  );
};

const Swipeable = (props) => {
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      props.onSwipedLeftCallback();
    },
    onSwipedRight: () => {
      props.onSwipedRightCallback();
    },
  });
  const myRef = React.useRef();
  const refPassthrough = (el) => {
    handlers.ref(el);
    myRef.current = el;
  };
  return (
    <div {...handlers} ref={refPassthrough}>
      {props.children}
    </div>
  );
};

export default injectIntl(React.memo(ImageCarouselComponent));
